import { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Button,
  Callout,
  CalloutTitle,
  Icon,
  Spacer,
  Stack,
  TextCaption,
} from '@pypestream/design-system';

import { useBobcatFlag } from '../../hooks/use-bobcat-flag';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import type { ProStudioContext } from '../../xstate/prostudio.xstate.types';
import { solutionAssistantStore } from '../../stores/solution-assistant-store';
import { tableStore } from '../../stores/table-store';
import { sendUserMessage } from '../../xstate/app.xstate-utils';
import {
  knowledgeSetupBlocksPublish,
  knowledgeSetupState,
  readKnowledgeUploadAt,
  type KnowledgeSetupState,
} from '../../stores/utils/knowledge-corpus-setup';

export const SKIP_KNOWLEDGE_SEARCH_EFFECT =
  "Customers' in-scope questions will be answered without searching a knowledge base. You can add one later.";

/** Blocking copy for the Publish button when only the corpus holds it up. */
export const KNOWLEDGE_SEARCH_PUBLISH_TOOLTIP =
  'The Knowledge Agent has no knowledge base yet. Add one, or skip knowledge search, to publish.';

/** Blocking copy when files were added but the search index never appeared. */
export const KNOWLEDGE_SETUP_STALLED_TOOLTIP =
  "Knowledge base setup didn't finish. Skip knowledge search to publish, or contact support.";

const STALLED_MESSAGE =
  "Knowledge base setup didn't finish. Your files were saved, but the search index wasn't created.";

export const skipKnowledgeSearch = async () => {
  const changed = await tableStore.applySkipKnowledgeCorpus({ undoable: true });
  if (changed) {
    sendUserMessage({
      type: 'success',
      text: `Knowledge search skipped. ${SKIP_KNOWLEDGE_SEARCH_EFFECT}`,
      autoClose: 5000,
    });
  }
};

/** Bobcat-only: the onboarding flows that can skip the knowledge base. */
const useKnowledgeSearchToggleEnabled = () =>
  useBobcatFlag('fiveStageNav') === 'allowed';

// When this session first saw a solution's search waiting on a corpus it has
// files for — the clock for solutions with no recorded upload time.
const firstSeenWaiting = new Map<string, number>();
const TICK_MS = 15_000;

/**
 * Where the Knowledge Agent's knowledge base stands (see
 * `stores/utils/knowledge-corpus-setup.ts`): nothing waiting, never added,
 * still being set up, or set up but the search index never appeared.
 * Re-evaluates on a timer while setup is in progress, so "still being set
 * up" turns into "didn't finish" without a reload.
 */
export const useKnowledgeSetupState = (): KnowledgeSetupState => {
  const enabled = useKnowledgeSearchToggleEnabled();
  const hasFiles = useProStudioCtxSelector(
    (ctx: ProStudioContext) =>
      (ctx.draftVersionSolutionData?.files?.length ?? 0) > 0
  );
  const unprovisioned = tableStore.knowledgeSearchUnprovisioned;
  const versionId = tableStore.solutionVersionId;
  const [now, setNow] = useState(() => Date.now());

  const waitingWithFiles = enabled && unprovisioned && hasFiles;
  if (waitingWithFiles && versionId && !firstSeenWaiting.has(versionId)) {
    firstSeenWaiting.set(versionId, Date.now());
  }
  const state = enabled
    ? knowledgeSetupState({
        unprovisioned,
        hasFiles,
        uploadAt: readKnowledgeUploadAt(tableStore.rows),
        firstSeenAt: (versionId && firstSeenWaiting.get(versionId)) || null,
        now,
      })
    : 'ready';

  useEffect(() => {
    if (state !== 'provisioning') return undefined;
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [state]);

  return state;
};

// Solutions whose stalled setup this session already skipped.
const autoSkipped = new Set<string>();

export const AUTO_SKIPPED_MESSAGE =
  "Knowledge base setup didn't finish, so knowledge search was turned off and the solution can be published. Your files are saved. Upload them again in Agents › Knowledge Agent to turn search back on.";

/**
 * When the knowledge base's setup stalls (no search index after
 * `KNOWLEDGE_SETUP_TIMEOUT_MS`), skip the search on its own, as if the
 * solution had none, so Publish isn't held indefinitely, and say so. The backend
 * logs a failed index and stops (`create-corpuses-and-update-node-for-solution.ts`),
 * so nothing else would ever release it. Once per solution version; not for
 * viewers. Mounted once, by `builder.tsx`.
 */
export const useAutoSkipStalledKnowledge = () => {
  const state = useKnowledgeSetupState();
  const versionId = tableStore.solutionVersionId;
  useEffect(() => {
    if (state !== 'stalled' || !versionId || tableStore.isViewer) return;
    if (autoSkipped.has(versionId)) return;
    autoSkipped.add(versionId);
    void tableStore
      .applySkipKnowledgeCorpus({ undoable: true })
      .then((changed) => {
        if (changed) {
          sendUserMessage({
            type: 'info',
            text: AUTO_SKIPPED_MESSAGE,
            autoClose: 10000,
          });
        }
      });
  }, [state, versionId]);
};

/**
 * True when Publish is held by the knowledge search with a clear reason: a
 * knowledge base that was never added, or one whose setup didn't finish.
 * While files are genuinely still being set up, the normal "still setting
 * things up" wait applies instead.
 */
export const useKnowledgeSearchBlocksPublish = (): boolean =>
  knowledgeSetupBlocksPublish(useKnowledgeSetupState());

/** The Publish tooltip for a knowledge-search hold, or `undefined`. */
export const useKnowledgeSearchPublishTooltip = (): string | undefined => {
  const state = useKnowledgeSetupState();
  if (state === 'stalled') return KNOWLEDGE_SETUP_STALLED_TOOLTIP;
  if (state === 'no-knowledge-base') return KNOWLEDGE_SEARCH_PUBLISH_TOOLTIP;
  return undefined;
};

/**
 * Shown while the Knowledge Agent's RAG lookup waits on a knowledge base
 * that was never added — the case that holds Publish. Offers the two ways
 * out: skip the search, or add a knowledge base (Agents › Knowledge Agent).
 * `inAgentsPanel` drops the "add" link — the upload is right below it there.
 */
export const KnowledgeSearchNotice: FC<{
  inAgentsPanel?: boolean;
  viewOnly?: boolean;
}> = observer(({ inAgentsPanel, viewOnly }) => {
  const assistantEnabled = useBobcatFlag('fiveStageNav') === 'allowed';
  const state = useKnowledgeSetupState();
  if (!knowledgeSetupBlocksPublish(state)) return null;
  const stalled = state === 'stalled';
  return (
    <>
      <Callout
        size="small"
        theme="light"
        test-id={
          stalled
            ? 'bobcat-knowledge-setup-stalled'
            : 'bobcat-knowledge-search-notice'
        }
      >
        <Stack alignItems="start" gutter="xsmall" direction="column">
          <Stack alignItems="center" gutter="xsmall">
            <Icon name="info" size="small" weight="medium" />
            <CalloutTitle>
              {stalled
                ? "Knowledge base setup didn't finish"
                : 'No knowledge base yet'}
            </CalloutTitle>
          </Stack>
          {stalled ? (
            <TextCaption>
              {STALLED_MESSAGE} Publishing is on hold. Skip knowledge search to
              publish without it (your files stay saved), or contact support to
              finish setting it up.
            </TextCaption>
          ) : (
            <TextCaption>
              The Knowledge Agent searches a knowledge base that hasn&apos;t
              been added, so publishing is on hold.{' '}
              {inAgentsPanel
                ? 'Upload files below and save, or skip the search.'
                : 'Add one, or skip the search.'}
            </TextCaption>
          )}
          {!viewOnly && (
            <>
              <Spacer size="xsmall" />
              <Stack gutter="xsmall">
                <Button
                  size="small"
                  variant="secondary"
                  test-id="bobcat-skip-knowledge-search"
                  title={SKIP_KNOWLEDGE_SEARCH_EFFECT}
                  onClick={skipKnowledgeSearch}
                >
                  Skip knowledge search
                </Button>
                {!stalled && !inAgentsPanel && assistantEnabled && (
                  <Button
                    size="small"
                    variant="ghost"
                    test-id="bobcat-add-knowledge-base"
                    onClick={() =>
                      solutionAssistantStore.openAgents('knowledge-agent')
                    }
                  >
                    Add a knowledge base
                  </Button>
                )}
              </Stack>
              <TextCaption variant="secondary">
                Skipping: {SKIP_KNOWLEDGE_SEARCH_EFFECT}
              </TextCaption>
            </>
          )}
        </Stack>
      </Callout>
      {/* In the Agents panel the upload field sits right below this notice. */}
      {inAgentsPanel && <Spacer />}
    </>
  );
});

/**
 * Shown in Agents › Knowledge Agent once the search was skipped: adding
 * files turns it back on when the panel is saved.
 */
export const KnowledgeSearchSkippedNote: FC = observer(() => {
  const enabled = useKnowledgeSearchToggleEnabled();
  if (!enabled || !tableStore.knowledgeSearchRestorable) return null;
  // Both places that render this note put the upload field right below it,
  // so the gap lives here and shows only while the note does.
  return (
    <>
      <Callout size="small" theme="light" test-id="bobcat-knowledge-search-off">
        <Stack alignItems="center" gutter="xsmall">
          <Icon name="info" size="small" weight="medium" />
          <CalloutTitle>
            Knowledge search is off. Upload files and save to turn it back on.
          </CalloutTitle>
        </Stack>
      </Callout>
      <Spacer />
    </>
  );
});
